import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";
import {
  CreateConversationResponse,
  ListAccountsResponse,
  ListChildrenResponse,
  ListConversationsResponse,
  ListMessagesResponse,
  ListMountQueuesResponse,
  ListMountsResponse,
  MountQueue,
  ListWireResponse,
  OpenDownloadResponse,
  ReadFileResponse,
  RenameConversationResponse,
  RescanMountResponse,
  SearchResponse,
  SendMessageResponse,
  SetMountStatusResponse,
  StatNodeResponse,
} from "../pages/loom/types";

/**
 * Loom 2.0 RPC client — aichat.v1, vfs.v1 and rag.v1.
 *
 * All three engines run in the same mesh process the `continuum` logical name
 * resolves to, so one client and one mailbox serves them.
 *
 * Authorization is by SENDER MAILBOX, not a per-call token: rawRpcCall stamps
 * the user's mailbox on the frame and godev resolves the caller from it. That
 * is why these calls must ride the user's own WS session — and why the vfs
 * ACL check server-side sees the real person rather than a service identity.
 *
 * Wire notes: contentType MUST be Json (the default is Binary, and godev then
 * takes the proto branch and fails to decode). Send lowerCamelCase ONLY —
 * protojson rejects a field supplied twice, so never send both spellings.
 */
// SendMessage returns only when the whole turn is done, and a turn runs up to
// 24 tool rounds against the LLM, so a healthy turn can take minutes. This is
// the ceiling for that one call; every other Loom RPC keeps the client default.
export const SEND_MESSAGE_TIMEOUT_MS = 15 * 60_000;

class LoomRpcClient {
  private targetMailbox: MailboxAddress | null = null;

  private async getTargetMailbox(): Promise<MailboxAddress> {
    if (!this.targetMailbox) {
      this.targetMailbox = await nameMappingService.resolve(
        asLogicalMailbox("continuum"),
      );
    }
    return this.targetMailbox;
  }

  private async rpcCall<TResp>(
    endPoint: string,
    request: Record<string, unknown>,
    timeoutMs?: number,
  ): Promise<TResp> {
    const client = GlobalClient.get();
    const targetMailbox = await this.getTargetMailbox();

    const rpcResponse = await client.rawRpcCall({
      to: targetMailbox,
      endPoint,
      body: new TextEncoder().encode(JSON.stringify(request)),
      contentType: ContentType.Json,
      timeoutMs,
    });

    const frameType = rpcResponse.header?.rpcHeader?.frameType;
    if (frameType === RpcFrameType.ErrorResponse) {
      const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
      throw new Error(
        errorInfo?.message ||
          errorInfo?.errorNode?.frame?.message ||
          `RPC call to ${endPoint} failed`,
      );
    }

    if (!rpcResponse.data || rpcResponse.data.length === 0) {
      return {} as TResp;
    }
    return JSON.parse(new TextDecoder().decode(rpcResponse.data)) as TResp;
  }

  // -------------------------------------------------------------- aichat.v1

  async createConversation(args: {
    /** folder under /chats; omitted = the caller's own folder, created on first use */
    folder?: string;
    title: string;
    llmAccountUid: string;
    model: string;
    /**
     * bind the conversation to a page of this app (its /page/<name> segment):
     * each turn is briefed with the page's source and page_propose may save
     * under that name only (FEATURE-20261012-page-bound-chat-drawer-and-source-tab)
     */
    pageName?: string;
    /**
     * bind the conversation to a checkpoint fileset instead: each turn is briefed
     * with the caller's draft of it and fileset_propose writes that draft
     * (FEATURE-20261023-checkpoint-fileset-chat-with-drafts)
     */
    filesetUid?: string;
  }): Promise<CreateConversationResponse> {
    return this.rpcCall<CreateConversationResponse>(
      "aichat.v1.CreateConversation",
      args,
    );
  }

  async sendMessage(
    conversationUid: string,
    text: string,
    images: { mediaType: string; data: string }[] = [],
  ): Promise<SendMessageResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>(
      "aichat.v1.SendMessage",
      images.length > 0
        ? { conversationUid, text, images }
        : { conversationUid, text },
      SEND_MESSAGE_TIMEOUT_MS,
    );
    return {
      reply: (raw.reply as string) ?? "",
      messageUid: (raw.messageUid as string) ?? "",
      model: (raw.model as string) ?? "",
      // int64 crosses protojson as a string
      inputTokens: Number(raw.inputTokens ?? 0),
      outputTokens: Number(raw.outputTokens ?? 0),
      errorMessage: raw.errorMessage as string | undefined,
    };
  }

  /**
   * sendMessage detached (FEATURE-20261014-page-chat-hardening, stage 2): returns once
   * the user's message is stored; the tool rows and the reply land in the
   * conversation as the turn runs (watch it with onConversationChange, or
   * listMessages). One turn per conversation at a time.
   */
  async startTurn(
    conversationUid: string,
    text: string,
    images: { mediaType: string; data: string }[] = [],
  ): Promise<{ messageUid: string; errorMessage?: string }> {
    const raw = await this.rpcCall<Record<string, unknown>>(
      "aichat.v1.StartTurn",
      images.length > 0 ? { conversationUid, text, images } : { conversationUid, text },
    );
    return { messageUid: (raw.messageUid as string) ?? "", errorMessage: raw.errorMessage as string | undefined };
  }

  /** stop the turn running in a conversation; it ends with a reply saying so */
  async cancelTurn(conversationUid: string): Promise<{ cancelled: boolean; errorMessage?: string }> {
    const raw = await this.rpcCall<Record<string, unknown>>("aichat.v1.CancelTurn", { conversationUid });
    return { cancelled: Boolean(raw.cancelled), errorMessage: raw.errorMessage as string | undefined };
  }

  async listMessages(conversationUid: string): Promise<ListMessagesResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>(
      "aichat.v1.ListMessages",
      { conversationUid },
    );
    const messages = ((raw.messages as Record<string, unknown>[]) ?? []).map(
      (m) => ({
        uid: (m.uid as string) ?? "",
        seq: Number(m.seq ?? 0),
        role: (m.role as string) ?? "",
        content: (m.content as string) ?? "",
        authorUid: (m.authorUid as string) ?? "",
        model: (m.model as string) ?? "",
        imagePaths: (m.imagePaths as string[]) ?? [],
      }),
    );
    return { messages, errorMessage: raw.errorMessage as string | undefined };
  }

  /**
   * The dig-deeper query: every provider call behind the conversation with
   * its captured request/response (FEATURE-20260803-loom2-chat-wire-visibility).
   */
  async listWire(conversationUid: string): Promise<ListWireResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>(
      "aichat.v1.ListWire",
      { conversationUid },
    );
    const calls = ((raw.calls as Record<string, unknown>[]) ?? []).map((c) => ({
      llmCallUid: (c.llmCallUid as string) ?? "",
      createdAt: (c.createdAt as string) ?? "",
      purpose: (c.purpose as string) ?? "",
      round: Number(c.round ?? 0),
      turnMessageUid: (c.turnMessageUid as string) ?? "",
      messageUid: (c.messageUid as string) ?? "",
      model: (c.model as string) ?? "",
      inputTokens: Number(c.inputTokens ?? 0),
      outputTokens: Number(c.outputTokens ?? 0),
      latency: (c.latency as string) ?? "",
      error: (c.error as string) ?? "",
      requestJson: (c.requestJson as string) ?? "",
      responseJson: (c.responseJson as string) ?? "",
      cacheReadTokens: Number(c.cacheReadTokens ?? 0),
      cacheWriteTokens: Number(c.cacheWriteTokens ?? 0),
      costMicrousd: Number(c.costMicrousd ?? 0),
      unpriced: Boolean(c.unpriced),
    }));
    return { calls, errorMessage: raw.errorMessage as string | undefined };
  }

  async listAccounts(): Promise<ListAccountsResponse> {
    return this.rpcCall<ListAccountsResponse>("aichat.v1.ListAccounts", {});
  }

  async listConversations(folder: string): Promise<ListConversationsResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>(
      "aichat.v1.ListConversations",
      { folder },
    );
    return {
      conversations: (
        (raw.conversations as Record<string, unknown>[]) ?? []
      ).map((c) => ({
        conversationUid: (c.conversationUid as string) ?? "",
        nodeUid: (c.nodeUid as string) ?? "",
        title: (c.title as string) ?? "",
        path: (c.path as string) ?? "",
        lastActivity: (c.lastActivity as string) ?? "",
        model: (c.model as string) ?? "",
        costMicrousd: Number(c.costMicrousd ?? 0),
        unpricedCalls: Number(c.unpricedCalls ?? 0),
      })),
      errorMessage: raw.errorMessage as string | undefined,
    };
  }

  async renameConversation(
    conversationUid: string,
    title: string,
  ): Promise<RenameConversationResponse> {
    return this.rpcCall<RenameConversationResponse>(
      "aichat.v1.RenameConversation",
      { conversationUid, title },
    );
  }

  // ----------------------------------------------------------------- vfs.v1

  async listMounts(): Promise<ListMountsResponse> {
    return this.rpcCall<ListMountsResponse>("vfs.v1.ListMounts", {});
  }

  /**
   * With the admin bypass off, every vfs.v1 read is sent grants_only: the
   * server treats the caller as a grant-only principal, which is what a chat
   * or a search sees for the same login
   * (FEATURE-20261002-vfs-viewer-shows-admin-bypass).
   */
  private grantsOnly = false;

  setAdminBypass(on: boolean): void {
    this.grantsOnly = !on;
  }

  private pathRequest(path: string): Record<string, unknown> {
    return this.grantsOnly ? { path, grantsOnly: true } : { path };
  }

  /** Listings ask for hidden nodes; the server lists them for an admin on the folder. */
  private showHidden = false;

  setShowHidden(on: boolean): void {
    this.showHidden = on;
  }

  async listChildren(path: string): Promise<ListChildrenResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>(
      "vfs.v1.ListChildren",
      this.showHidden
        ? { ...this.pathRequest(path), showHidden: true }
        : this.pathRequest(path),
    );
    return {
      nodes: ((raw.nodes as Record<string, unknown>[]) ?? []).map((n) => ({
        uid: (n.uid as string) ?? "",
        path: (n.path as string) ?? "",
        name: (n.name as string) ?? "",
        kind: (n.kind as string) ?? "",
        size: Number(n.size ?? 0),
        remoteMtime: (n.remoteMtime as string) ?? "",
        ingestStatus: (n.ingestStatus as string) ?? "",
        aclNodeUid: (n.aclNodeUid as string) ?? "",
        access: (n.access as string) ?? "",
        hidden: n.hidden === true,
        optionsError: (n.optionsError as string) ?? "",
        description: (n.description as string) ?? "",
        tags: (n.tags as string[]) ?? [],
        noIndex: n.noIndex === true,
      })),
      errorMessage: raw.errorMessage as string | undefined,
    };
  }

  /** Rescans the mount holding path: a host-watch mount through its watcher. */
  async rescanMount(path: string): Promise<RescanMountResponse> {
    return this.rpcCall<RescanMountResponse>("vfs.v1.RescanMount", { path });
  }

  /**
   * Every mount the caller may read, with its ingest queue
   * (FEATURE-20261002-watcher-controls-and-queue-visibility). protojson sends
   * int64 as strings, so every count is converted here, once.
   */
  async listMountQueues(): Promise<ListMountQueuesResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>("vfs.v1.ListMountQueues", {});
    const str = (m: Record<string, unknown>, k: string) => (m[k] as string) ?? "";
    const num = (m: Record<string, unknown>, k: string) => Number(m[k] ?? 0);
    return {
      mounts: ((raw.mounts as Record<string, unknown>[]) ?? []).map(
        (m): MountQueue => ({
          uid: str(m, "uid"),
          pathPrefix: str(m, "pathPrefix"),
          backendType: str(m, "backendType"),
          status: str(m, "status"),
          lastSweepAt: str(m, "lastSweepAt"),
          files: num(m, "files"),
          pending: num(m, "pending"),
          retryDue: num(m, "retryDue"),
          failedWaiting: num(m, "failedWaiting"),
          awaitingSidecar: num(m, "awaitingSidecar"),
          indexed: num(m, "indexed"),
          skipped: num(m, "skipped"),
          oldestPendingAt: str(m, "oldestPendingAt"),
          lastIndexedAt: str(m, "lastIndexedAt"),
          currentError: str(m, "currentError"),
          currentErrorFiles: num(m, "currentErrorFiles"),
          watcherPrincipalUid: str(m, "watcherPrincipalUid"),
          watcherRegistered: Boolean(m["watcherRegistered"]),
          watcherMailbox: str(m, "watcherMailbox"),
          watcherAnswered: Boolean(m["watcherAnswered"]),
          watcherError: str(m, "watcherError"),
          watcherRoot: str(m, "watcherRoot"),
          watcherIndexedPaths: num(m, "watcherIndexedPaths"),
          watcherImagesPending: num(m, "watcherImagesPending"),
          watcherLastScanAt: str(m, "watcherLastScanAt"),
        }),
      ),
      changeEventsWaiting: Number(raw.changeEventsWaiting ?? 0),
      errorMessage: raw.errorMessage as string | undefined,
    };
  }

  /** Switches a mount active or inactive (admin on the mount root, or ContinuumAdmin). */
  async setMountStatus(mountUid: string, status: "active" | "inactive"): Promise<SetMountStatusResponse> {
    return this.rpcCall<SetMountStatusResponse>("vfs.v1.SetMountStatus", { mountUid, status });
  }

  async statNode(path: string): Promise<StatNodeResponse> {
    return this.rpcCall<StatNodeResponse>("vfs.v1.StatNode", this.pathRequest(path));
  }

  async readFile(path: string): Promise<ReadFileResponse> {
    return this.rpcCall<ReadFileResponse>("vfs.v1.ReadFile", this.pathRequest(path));
  }

  /**
   * Authorize a download and get back a capability URL. The bytes come from
   * the blob data plane over ordinary HTTP — never through this client — so
   * the caller navigates to the returned url rather than buffering it
   * (FEATURE-20260803-vfs-download-via-blob).
   */
  async openDownload(path: string): Promise<OpenDownloadResponse> {
    return this.rpcCall<OpenDownloadResponse>("vfs.v1.OpenDownload", this.pathRequest(path));
  }

  // ----------------------------------------------------------------- rag.v1

  async search(query: string, limit = 8): Promise<SearchResponse> {
    const raw = await this.rpcCall<Record<string, unknown>>("rag.v1.Search", {
      query,
      limit,
    });
    return {
      hits: ((raw.hits as Record<string, unknown>[]) ?? []).map((h) => ({
        nodeUid: (h.nodeUid as string) ?? "",
        path: (h.path as string) ?? "",
        seq: Number(h.seq ?? 0),
        text: (h.text as string) ?? "",
        similarity: Number(h.similarity ?? 0),
      })),
      errorMessage: raw.errorMessage as string | undefined,
    };
  }
}

let instance: LoomRpcClient | null = null;

export function getLoomRpcClient(): LoomRpcClient {
  if (!instance) {
    instance = new LoomRpcClient();
  }
  return instance;
}
